import React from 'react';
import * as Yup from 'yup';
import { JFormikData, selectUser, sortString } from '@centerpiece/components';
import { FormikHelpers, getIn } from 'formik';
import { statesConfig } from '@centerpiece/components';
import {
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import {
  RestartAlt,
  MailOutlineOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  NotesOutlined,
  CorporateFareOutlined,
  Key,
  Groups,
  InfoOutlined,
} from '@mui/icons-material';
import {
  useCreateRoleMutation,
  useGetContactQuery,
  useGetRolesQuery,
  useGetTeamsQuery,
} from 'store/api';
import { useSelector } from 'react-redux';
import { Contact } from 'store/api/models';

export const useOrganizationForm = ({
  edit = false,
  minimal = false,
  externalSubmit = false,
  role = true,
  account = false,
  roleType = 'External',
  organizations,
}: {
  edit?: boolean;
  minimal?: boolean;
  externalSubmit?: boolean;
  role?: boolean;
  account?: boolean;
  roleType?: 'External';
  organizations?: string[];
}): JFormikData => {
  const user = useSelector(selectUser);
  const { data: contact = {} as Contact, isLoading } = useGetContactQuery(
    { id: user.contact },
    { skip: !user.contact }
  );
  const admin = contact?.form?.admin === 'Yes';
  const { data: roles = [] } = useGetRolesQuery({ params: { type: roleType } });
  const { data: teams = [] } = useGetTeamsQuery({});
  const [createRole] = useCreateRoleMutation();
  return {
    validationSchema: Yup.object({
      name: Yup.string().required('Organization name is required'),
      role: Yup.object().required('Please select an option').nullable(),
    }),
    form: [
      {
        precondition: (values: any) => !minimal,
        name: 'basic-fields',
        itemType: 'typography',
        jItemProps: {
          variant: 'overline',
          sx: { mb: '0 !important', pb: '0 !important' },
        },
        label: 'Basic Information',
      },
      {
        precondition: (values: any) => !minimal,
        name: 'basic-fields-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        name: 'name',
        label: 'Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        name: 'role',
        label: 'Organization Type',
        itemType: 'autocomplete',
        options: [...(roles || [])].sort((a, b) => sortString(a.name, b.name)),
        // freeSolo: true,
        jItemProps: {
          // freeSolo: true,
          addValue: async (
            name: any,
            newValue: any,
            values: any,
            formik: FormikHelpers<any>
          ) => {
            const res = await createRole({
              name: newValue?.inputValue,
              type: roleType,
            }).unwrap();
            formik.setFieldValue(name, res);
          },
          width: '25%',
          sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 130 },
        },
        gridProps: { xs: 12 },
      },

      ...(minimal
        ? []
        : [
            {
              precondition: (values: any) => !minimal,
              name: 'additional-fields',
              itemType: 'typography',
              jItemProps: {
                variant: 'overline',
                sx: { mb: '0 !important', pb: '0 !important' },
              },
              label: 'Additional Fields',
            },
            {
              precondition: (values: any) => !minimal,
              name: 'additional-fields-bottom',
              label: '',
              itemType: 'divider',
              gridProps: { sx: { paddingTop: '0px !important' } },
            },
            {
              precondition: (values: any) =>
                !minimal ||
                (values?.form?.custom_fields &&
                  values?.form?.custom_fields?.length > 0),
              name: 'form.custom_fields',
              itemType: 'fieldarray',
              label: 'Additional Field',
              gridProps: {
                // sx: { paddingTop: '10px !important' },
              },
              array: [
                {
                  precondition: (values: any) =>
                    !values?.form?.custom_fields?.[values.index]?.fieldtype,
                  name: 'fieldtype',
                  label: 'Type',
                  // disabled: edit,
                  jItemProps: edit ? { type: 'local-disabled' } : {},
                  itemType: 'autocomplete',
                  options: ['Phone (Office)', 'Address', 'Notes', 'Custom'],
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Custom',
                  name: 'dynamic',
                  label: 'Custom Field',
                  ...(!edit
                    ? {
                        tooltip: {
                          title: (
                            <Typography variant="body1">
                              Please specify the title of the field
                            </Typography>
                          ),
                          open: true,
                          placement: 'top',
                          arrow: true,
                        },
                      }
                    : {}),
                  jItemProps: {
                    textFieldType: 'custom',
                    // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
                    //   return getIn(values, name) || 'Please enter the field label...'
                    // },
                    InputProps: {
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)?.value) {
                          return (
                            <InputAdornment position="end">
                              <Tooltip title={`Reset type`}>
                                <IconButton
                                  onClick={() => {
                                    formik.setFieldValue(name, undefined);
                                    formik.setFieldValue(
                                      name.replaceAll('dynamic', 'fieldtype'),
                                      undefined
                                    );
                                  }}
                                >
                                  <RestartAlt color="secondary" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Job Title',
                  name: 'job_title',
                  label: 'Job Title',
                  jItemProps: {
                    InputProps: {
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('job_title', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Notes',
                  name: 'notes',
                  label: 'Notes',
                  jItemProps: {
                    multiline: true,
                    rows: 4,
                    variant: 'outlined',
                    InputProps: {
                      endAdornment: <NotesOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('notes', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Phone (Mobile)',
                  name: 'phone_mobile',
                  label: 'Phone (Mobile)',
                  // disabled: edit,
                  jItemProps: {
                    type: 'phone',
                    InputProps: {
                      endAdornment: <PhoneOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('phone', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Phone (Office)',
                  name: 'phone_office',
                  label: 'Phone (Office)',
                  // disabled: edit,
                  jItemProps: {
                    type: 'phone',
                    InputProps: {
                      endAdornment: <PhoneOutlined color="secondary" />,
                      startAdornment: (
                        name: any,
                        values: any,
                        formik: FormikHelpers<any>
                      ) => {
                        if (!getIn(values, name)) {
                          return (
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() =>
                                  formik.setFieldValue(
                                    name.replaceAll('phone', 'fieldtype'),
                                    undefined
                                  )
                                }
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          );
                        }
                      },
                    },
                  },
                  itemType: 'textfield',
                },
                {
                  precondition: (values: any) =>
                    values?.form?.custom_fields?.[values.index]?.fieldtype ===
                    'Address',
                  name: '',
                  label: '',
                  itemType: 'paper',
                  jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
                  array: [
                    {
                      name: 'address-label',
                      label: 'Address',
                      itemType: 'typography',
                      jItemProps: { variant: 'overline' },
                    },
                    {
                      name: 'address-label-bottom',
                      label: '',
                      itemType: 'divider',
                      gridProps: { sx: { paddingTop: '0px !important' } },
                    },
                    {
                      name: 'form.address',
                      itemType: 'textfield',
                      label: 'Address',
                      gridProps: { xs: 12 },
                    },
                    {
                      name: 'form.city',
                      itemType: 'textfield',
                      label: 'City',
                      gridProps: { xs: 12, sm: 4 },
                    },
                    {
                      name: 'form.state',
                      label: 'State',
                      itemType: 'autocomplete',
                      options: Object.keys(statesConfig),
                      gridProps: { xs: 12, sm: 4 },
                    },
                    {
                      name: 'form.zipcode',
                      itemType: 'textfield',
                      label: 'Postal Code',
                      gridProps: { xs: 12, sm: 4 },
                    },
                  ],
                },
              ],
            },
            {
              precondition: (values: any) => !externalSubmit,
              name: 'submit',
              itemType: 'button',
              jItemProps: { type: 'submit' },
              label: `${edit ? 'SAVE' : 'CREATE'} ORGANIZATION`,
              gridProps: { xs: 12, sm: 6, md: 5, mx: 'auto' },
            },
          ]),
    ],
  } as JFormikData;
};

export const useOrganizationTooltip = (): JFormikData => {
  return {
    validationSchema: Yup.object(),
    form: [
      {
        name: 'name',
        label: 'Name',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          InputProps: {
            endAdornment: <PersonOutlineOutlined color="secondary" />,
          },
        },
      },
      {
        name: 'role',
        label: 'Organization Type',
        itemType: 'autocomplete',
        // freeSolo: true,
        jItemProps: {
          width: '25%',
          sx: { boxSizing: 'border-box', px: { xs: 0.5 }, minWidth: 130 },
        },
        gridProps: { xs: 12 },
      },

      {
        precondition: (values: any) =>
          values?.form?.custom_fields &&
          values?.form?.custom_fields?.length > 0,
        name: 'form.custom_fields',
        itemType: 'fieldarray',
        label: 'Additional Field',
        gridProps: {
          // sx: { paddingTop: '10px !important' },
        },
        array: [
          {
            precondition: (values: any) =>
              !values?.form?.custom_fields?.[values.index]?.fieldtype,
            name: 'fieldtype',
            label: 'Type',
            // disabled: edit,
            itemType: 'autocomplete',
            options: ['Phone (Office)', 'Address', 'Notes', 'Custom'],
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Custom',
            name: 'dynamic',
            label: 'Custom Field',
            jItemProps: {
              textFieldType: 'custom',
              // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
              //   return getIn(values, name) || 'Please enter the field label...'
              // },
              InputProps: {
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)?.value) {
                    return (
                      <InputAdornment position="end">
                        <Tooltip title={`Reset type`}>
                          <IconButton
                            onClick={() => {
                              formik.setFieldValue(name, undefined);
                              formik.setFieldValue(
                                name.replaceAll('dynamic', 'fieldtype'),
                                undefined
                              );
                            }}
                          >
                            <RestartAlt color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Job Title',
            name: 'job_title',
            label: 'Job Title',
            jItemProps: {
              InputProps: {
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('job_title', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Notes',
            name: 'notes',
            label: 'Notes',
            jItemProps: {
              multiline: true,
              rows: 4,
              variant: 'outlined',
              InputProps: {
                endAdornment: <NotesOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('notes', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Phone (Mobile)',
            name: 'phone_mobile',
            label: 'Phone (Mobile)',
            // disabled: edit,
            jItemProps: {
              type: 'phone',
              InputProps: {
                endAdornment: <PhoneOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('phone', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Phone (Office)',
            name: 'phone_office',
            label: 'Phone (Office)',
            // disabled: edit,
            jItemProps: {
              type: 'phone',
              InputProps: {
                endAdornment: <PhoneOutlined color="secondary" />,
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)) {
                    return (
                      <Tooltip title={`Reset type`}>
                        <IconButton
                          onClick={() =>
                            formik.setFieldValue(
                              name.replaceAll('phone', 'fieldtype'),
                              undefined
                            )
                          }
                        >
                          <RestartAlt color="secondary" />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Address',
            name: '',
            label: '',
            itemType: 'paper',
            jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
            array: [
              {
                name: 'form.address',
                itemType: 'textfield',
                label: 'Address',
                gridProps: { xs: 12 },
              },
              {
                name: 'form.city',
                itemType: 'textfield',
                label: 'City',
                gridProps: { xs: 12, sm: 4 },
              },
              {
                name: 'form.state',
                label: 'State',
                itemType: 'autocomplete',
                options: Object.keys(statesConfig),
                gridProps: { xs: 12, sm: 4 },
              },
              {
                name: 'form.zipcode',
                itemType: 'textfield',
                label: 'Postal Code',
                gridProps: { xs: 12, sm: 4 },
              },
            ],
          },
        ],
      },
    ],
  } as JFormikData;
};
